import { defineStore } from "pinia";
import {
  type userType,
  store,
  router,
  resetRouter,
  routerArrays,
  storageLocal
} from "../utils";
import {
  type UserResult,
  type RefreshTokenResult,
  getLogin,
  refreshTokenApi
} from "@/api/user";
import { useMultiTagsStoreHook } from "./multiTags";
import { type DataInfo, setToken, removeToken, userKey } from "@/utils/auth";

export const useUserStore = defineStore("pure-user", {
  state: (): userType => ({
    // 头像
    avatar: storageLocal().getItem<DataInfo<number>>(userKey)?.avatar ?? "",
    // 用户名
    username: storageLocal().getItem<DataInfo<number>>(userKey)?.username ?? "",
    // 昵称
    nickname: storageLocal().getItem<DataInfo<number>>(userKey)?.nickname ?? "",
    // 页面级别权限
    roles: storageLocal().getItem<DataInfo<number>>(userKey)?.roles ?? [],
    // 按钮级别权限
    permissions:
      storageLocal().getItem<DataInfo<number>>(userKey)?.permissions ?? [],
    // 是否勾选了登录页的免登录
    isRemembered: false,
    // 登录页的免登录存储几天，默认7天
    loginDay: 7
  }),
  actions: {
    /** 存储头像 */
    SET_AVATAR(avatar: string) {
      this.avatar = avatar;
    },
    /** 存储用户名 */
    SET_USERNAME(username: string) {
      this.username = username;
    },
    /** 存储昵称 */
    SET_NICKNAME(nickname: string) {
      this.nickname = nickname;
    },
    /** 存储角色 */
    SET_ROLES(roles: Array<string>) {
      this.roles = roles;
    },
    /** 存储按钮级别权限 */
    SET_PERMS(permissions: Array<string>) {
      this.permissions = permissions;
    },
    /** 存储是否勾选了登录页的免登录 */
    SET_ISREMEMBERED(bool: boolean) {
      this.isRemembered = bool;
    },
    /** 设置登录页的免登录存储几天 */
    SET_LOGINDAY(value: number) {
      this.loginDay = Number(value);
    },
    /** 登入 */
    async loginByUsername(data) {
      return new Promise<UserResult>((resolve, reject) => {
        getLogin(data)
          .then(data => {
            if (data?.success) setToken(data.data);
            resolve(data);
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    /** 前端登出（不调用接口） */
    logOut() {
      this.username = "";
      this.roles = [];
      this.permissions = [];
      removeToken();
      useMultiTagsStoreHook().handleTags("equal", [...routerArrays]);
      resetRouter();

      // 清除浏览器所有缓存
      localStorage.clear();
      sessionStorage.clear();

      // 清除所有 cookies
      const clearAllCookies = () => {
        const cookies = document.cookie.split(";");
        const hostname = window.location.hostname;
        const paths = ["/"];

        // 添加当前路径的所有父路径
        const currentPath = window.location.pathname;
        const pathParts = currentPath.split("/").filter(Boolean);
        for (let i = 0; i < pathParts.length; i++) {
          paths.push("/" + pathParts.slice(0, i + 1).join("/"));
        }

        // 获取所有可能的域名组合
        const getDomains = () => {
          const domains = ["", hostname];
          const parts = hostname.split(".");

          // 多级域名处理
          if (parts.length >= 2) {
            domains.push("." + hostname);
            // 添加所有可能的父域名
            for (let i = 1; i < parts.length; i++) {
              const domain = parts.slice(i).join(".");
              domains.push(domain, "." + domain);
            }
          }

          return [...new Set(domains)];
        };

        const domains = getDomains();

        // 删除所有 cookies
        cookies.forEach(cookie => {
          const eqPos = cookie.indexOf("=");
          const name = (
            eqPos > -1 ? cookie.substring(0, eqPos) : cookie
          ).trim();

          if (!name) return;

          // 尝试所有可能的路径和域组合
          paths.forEach(path => {
            domains.forEach(domain => {
              const cookieString = domain
                ? `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}`
                : `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
              document.cookie = cookieString;
            });
          });
        });
      };

      clearAllCookies();

      // 清除项目使用的 IndexedDB 数据库
      if (window.indexedDB) {
        const dbNames = ["pure-admin"]; // localforage 配置的数据库名

        dbNames.forEach(dbName => {
          const deleteRequest = indexedDB.deleteDatabase(dbName);
          deleteRequest.onerror = () => {
            console.warn(`清除 IndexedDB ${dbName} 失败`);
          };
        });

        // 清除其他可能存在的 IndexedDB 数据库
        if (indexedDB.databases) {
          indexedDB
            .databases()
            .then(databases => {
              databases.forEach(db => {
                if (db.name && !dbNames.includes(db.name)) {
                  indexedDB.deleteDatabase(db.name);
                }
              });
            })
            .catch(error => {
              console.warn("获取 IndexedDB 列表失败:", error);
            });
        }
      }

      // 清除 Cache Storage
      if ("caches" in window) {
        caches
          .keys()
          .then(names => {
            names.forEach(name => {
              caches.delete(name);
            });
          })
          .catch(error => {
            console.warn("清除 Cache Storage 失败:", error);
          });
      }

        router.push("/login");
    },
    /** 刷新`token` */
    async handRefreshToken(data) {
      return new Promise<RefreshTokenResult>((resolve, reject) => {
        refreshTokenApi(data)
          .then(data => {
            if (data) {
              setToken(data.data);
              resolve(data);
            }
          })
          .catch(error => {
            reject(error);
          });
      });
    }
  }
});

export function useUserStoreHook() {
  return useUserStore(store);
}
